import {
  NO_UPDATE_LIMIT_KEYS,
  NO_UPDATE_LIMIT_LABELS,
  type NoUpdateLimitKey,
  type TrackingSettings,
  type TrackingSettingsPatch,
  type TrackingSettingsState,
} from "@estelle/domain";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useId, useState, type FormEvent, type ReactNode } from "react";

import { useDataSource } from "@/adapters/data-source";
import { can, usePermissions } from "@/auth/permissions";
import { LoadFailed } from "@/components/shell/EmptyState";
import { TableSkeleton } from "@/components/shell/LoadingState";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { ExactTime } from "@/modules/orders/screens/TrackingParts";
import { OutcomeLine } from "@/modules/settings/logistics/PartnerCards";
import type { OutcomeMessage } from "@/modules/orders/tracking";

/** What the form holds while someone types: text, so a half-typed number is not lost. */
interface Draft {
  pollIntervalMinutes: string;
  noUpdateHours: Record<NoUpdateLimitKey, string>;
  skipSundays: boolean;
  stopAfterDays: string;
}

function toDraft(settings: TrackingSettings): Draft {
  return {
    pollIntervalMinutes: String(settings.pollIntervalMinutes),
    noUpdateHours: Object.fromEntries(
      NO_UPDATE_LIMIT_KEYS.map((key) => [
        key,
        String(settings.noUpdateHours[key]),
      ]),
    ) as Record<NoUpdateLimitKey, string>,
    skipSundays: settings.skipSundays,
    stopAfterDays: String(settings.stopAfterDays),
  };
}

/** What a typed value means: its number, or NaN for anything the server should name. */
function numberOf(text: string): number {
  const trimmed = text.trim();
  return trimmed === "" ? Number.NaN : Number(trimmed);
}

/**
 * Only what differs from what is in force. A value the person typed and the
 * server will refuse is sent as it is (`NaN` travels as `null`), so the answer
 * is the server's own message for that field.
 */
function patchOf(
  draft: Draft,
  current: TrackingSettings,
): TrackingSettingsPatch {
  const patch: TrackingSettingsPatch = {};
  const interval = numberOf(draft.pollIntervalMinutes);
  if (!Object.is(interval, current.pollIntervalMinutes)) {
    patch.pollIntervalMinutes = interval;
  }
  const hours: Partial<Record<NoUpdateLimitKey, number>> = {};
  for (const key of NO_UPDATE_LIMIT_KEYS) {
    const value = numberOf(draft.noUpdateHours[key]);
    if (!Object.is(value, current.noUpdateHours[key])) hours[key] = value;
  }
  if (Object.keys(hours).length > 0) patch.noUpdateHours = hours;
  if (draft.skipSundays !== current.skipSundays) {
    patch.skipSundays = draft.skipSundays;
  }
  const stop = numberOf(draft.stopAfterDays);
  if (!Object.is(stop, current.stopAfterDays)) patch.stopAfterDays = stop;
  return patch;
}

const same = (a: Draft, b: Draft) => JSON.stringify(a) === JSON.stringify(b);

/**
 * What the server would refuse, found here first (#553): a value that is not a
 * whole number inside the bounds the form already states beside each field.
 * Sent anyway, it came back as a 422 and a red failed request in the console for
 * what is an ordinary typing slip. Same field keys as the server's `fields`.
 */
function problemsOf(
  draft: Draft,
  bounds: TrackingSettingsState["bounds"],
): Record<string, string> {
  const problems: Record<string, string> = {};
  const check = (
    field: string,
    text: string,
    range: { min: number; max: number },
  ) => {
    const value = numberOf(text);
    if (!Number.isInteger(value) || value < range.min || value > range.max) {
      problems[field] =
        `Enter a whole number from ${String(range.min)} to ${String(range.max)}.`;
    }
  };
  check(
    "pollIntervalMinutes",
    draft.pollIntervalMinutes,
    bounds.pollIntervalMinutes,
  );
  for (const key of NO_UPDATE_LIMIT_KEYS) {
    check(
      `noUpdateHours.${key}`,
      draft.noUpdateHours[key],
      bounds.noUpdateHours,
    );
  }
  check("stopAfterDays", draft.stopAfterDays, bounds.stopAfterDays);
  return problems;
}

/** "180 minutes (3 hours)" — a default a person can compare with. */
function minutesWords(minutes: number): string {
  if (minutes % 60 !== 0) return `${String(minutes)} minutes`;
  const hours = minutes / 60;
  return `${String(minutes)} minutes (${String(hours)} hour${hours === 1 ? "" : "s"})`;
}

function Field({
  id,
  label,
  unit,
  value,
  hint,
  error,
  readOnly,
  onChange,
}: {
  id: string;
  label: string;
  unit: string;
  value: string;
  hint: string;
  error: string | undefined;
  readOnly: boolean;
  onChange: (value: string) => void;
}) {
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  return (
    <div className="space-y-1">
      <Label htmlFor={id} className="leading-snug">
        {label}
      </Label>
      <div className="flex items-center gap-2">
        <Input
          id={id}
          value={value}
          inputMode="numeric"
          enterKeyHint="done"
          autoComplete="off"
          readOnly={readOnly}
          aria-invalid={error !== undefined}
          aria-describedby={error ? `${hintId} ${errorId}` : hintId}
          className="read-only:bg-muted/50 w-28 tabular-nums pointer-coarse:h-11"
          onChange={(event) => {
            onChange(event.target.value);
          }}
        />
        <span className="text-muted-foreground text-sm">{unit}</span>
      </div>
      <p id={hintId} className="text-muted-foreground text-xs">
        {hint}
      </p>
      {error ? (
        <p id={errorId} role="alert" className="text-destructive text-sm">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function Form({ state }: { state: TrackingSettingsState }) {
  const data = useDataSource();
  const queryClient = useQueryClient();
  const permissions = usePermissions();
  const editable = can(permissions, "settings.company.manage");
  const formId = useId();

  const { settings, defaults, bounds, scheduler } = state;
  const initial = toDraft(settings);

  // The form starts from what is in force, and takes a new one when that
  // changes under it — somebody else saved — but only while nothing has been
  // typed. `held.initial` is what the form was last in step with: what it was
  // opened on, or what its own save came back with. A change is judged against
  // that and not against the settings last read, which lag a save by a request:
  // a person who edits a field straight after saving must not have it taken from
  // them when the settings are read back. Derived while rendering rather than in
  // an effect, so no frame shows the old values.
  const key = JSON.stringify(settings);
  const [held, setHeld] = useState({ key, initial, draft: initial });
  let draft = held.draft;
  let baseline = held.initial;
  if (held.key !== key) {
    baseline = initial;
    draft = same(held.draft, held.initial) ? initial : held.draft;
    setHeld({ key, initial, draft });
  }
  const setDraft = (next: Draft) => {
    setHeld((current) => ({ ...current, draft: next }));
  };

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<OutcomeMessage | null>(null);

  const save = useMutation({
    mutationFn: (submitted: Draft) =>
      data.tracking.saveSettings(patchOf(submitted, settings)),
    onSuccess: (result, submitted) => {
      if (result.ok) {
        // The form shows what is now in force (a typed "060" is 60), unless
        // something else was typed while the save was on its way.
        setHeld((current) => {
          if (!same(current.draft, submitted)) return current;
          const saved = toDraft(result.data.settings);
          return { ...current, initial: saved, draft: saved };
        });
        setErrors({});
        setMessage({
          tone: "ok",
          text: result.data.changed
            ? "Saved. Every open shipment's health is being judged against these limits now."
            : "Nothing changed: these are the settings already in force.",
        });
        void queryClient.invalidateQueries({ queryKey: ["tracking"] });
        // The lists show health: they are read again where they next open.
        void queryClient.invalidateQueries({ queryKey: ["orders"] });
        return;
      }
      const { rejection } = result;
      if (rejection.kind === "invalid") {
        setErrors(rejection.fields ?? {});
        setMessage({
          tone: "problem",
          text: `Not saved. ${rejection.message}`,
        });
        return;
      }
      setErrors({});
      setMessage({ tone: "problem", text: `Not saved. ${rejection.message}` });
    },
    onError: () => {
      setMessage({
        tone: "problem",
        text: "Could not save. Check your connection and try again.",
      });
    },
  });

  const dirty = !same(draft, baseline);
  const pending = save.isPending;

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!editable || pending || !dirty) return;
    const problems = problemsOf(draft, bounds);
    if (Object.keys(problems).length > 0) {
      setErrors(problems);
      setMessage({
        tone: "problem",
        text: "Not saved. Check the highlighted fields.",
      });
      return;
    }
    setMessage(null);
    save.mutate(draft);
  }

  const edit = (next: Draft, field: string) => {
    setDraft(next);
    setMessage(null);
    if (errors[field]) {
      setErrors((current) =>
        Object.fromEntries(
          Object.entries(current).filter(([name]) => name !== field),
        ),
      );
    }
  };

  return (
    <form onSubmit={submit} aria-label="Tracking settings" noValidate>
      <Card className="gap-5 py-5">
        <CardContent className="space-y-6 px-4 sm:px-6">
          {editable ? null : (
            <p
              role="note"
              className="bg-muted/60 text-muted-foreground rounded-md border p-3 text-sm"
            >
              You can see these settings but not change them. Changing them
              needs the Company settings permission; ask an administrator.
            </p>
          )}

          <Group
            title="How often to check"
            description="Every open shipment is asked about on this timer, through its partner or Shopify's scan history."
          >
            <Field
              id={`${formId}-interval`}
              label="Check every"
              unit="minutes"
              value={draft.pollIntervalMinutes}
              hint={`Default ${minutesWords(defaults.pollIntervalMinutes)}. From ${String(bounds.pollIntervalMinutes.min)} minutes to a day.`}
              error={errors.pollIntervalMinutes}
              readOnly={!editable || pending}
              onChange={(value) => {
                edit(
                  { ...draft, pollIntervalMinutes: value },
                  "pollIntervalMinutes",
                );
              }}
            />
            <p className="text-muted-foreground text-sm">
              {scheduler.enabled ? (
                scheduler.nextRunAt ? (
                  <>
                    Next scheduled check:{" "}
                    <ExactTime value={scheduler.nextRunAt} />.
                  </>
                ) : (
                  "The next scheduled check is being planned."
                )
              ) : (
                "The scheduled check is switched off on this server, so nothing is checked until someone uses Check now."
              )}
            </p>
          </Group>

          <Group
            title="When to flag “No update”"
            description="A shipment is flagged when its last scan is older than the limit for the stage it is in."
          >
            <div className="grid gap-4 sm:grid-cols-2">
              {NO_UPDATE_LIMIT_KEYS.map((limit) => (
                <Field
                  key={limit}
                  id={`${formId}-limit-${limit}`}
                  label={NO_UPDATE_LIMIT_LABELS[limit]}
                  unit="hours"
                  value={draft.noUpdateHours[limit]}
                  hint={`Default ${String(defaults.noUpdateHours[limit])} hours. From ${String(bounds.noUpdateHours.min)} to ${bounds.noUpdateHours.max.toLocaleString("en-IN")}.`}
                  error={errors[`noUpdateHours.${limit}`]}
                  readOnly={!editable || pending}
                  onChange={(value) => {
                    edit(
                      {
                        ...draft,
                        noUpdateHours: {
                          ...draft.noUpdateHours,
                          [limit]: value,
                        },
                      },
                      `noUpdateHours.${limit}`,
                    );
                  }}
                />
              ))}
            </div>
            {errors.noUpdateHours ? (
              <p role="alert" className="text-destructive text-sm">
                {errors.noUpdateHours}
              </p>
            ) : null}

            <div className="space-y-1">
              <Label
                htmlFor={`${formId}-sundays`}
                className="min-h-11 items-start gap-3 py-1 leading-snug font-normal"
              >
                <Checkbox
                  id={`${formId}-sundays`}
                  checked={draft.skipSundays}
                  disabled={!editable || pending}
                  aria-describedby={`${formId}-sundays-hint`}
                  className="mt-0.5 shrink-0"
                  onCheckedChange={(checked) => {
                    edit(
                      { ...draft, skipSundays: checked === true },
                      "skipSundays",
                    );
                  }}
                />
                <span className="font-medium">Do not count Sundays</span>
              </Label>
              <p
                id={`${formId}-sundays-hint`}
                className="text-muted-foreground text-xs"
              >
                Sundays in India are left out of the wait, so a parcel that went
                quiet on Saturday evening has had no working day to move in by
                Sunday evening. Default {defaults.skipSundays ? "on" : "off"}.
              </p>
              {errors.skipSundays ? (
                <p role="alert" className="text-destructive text-sm">
                  {errors.skipSundays}
                </p>
              ) : null}
            </div>
          </Group>

          <Group
            title="When to stop checking"
            description="A shipment with no scan at all for this long is checked once more, then marked “Tracking stopped, needs a look”."
          >
            <Field
              id={`${formId}-stop`}
              label="Stop checking after"
              unit="days"
              value={draft.stopAfterDays}
              hint={`Default ${String(defaults.stopAfterDays)} days. From ${String(bounds.stopAfterDays.min)} to ${String(bounds.stopAfterDays.max)}.`}
              error={errors.stopAfterDays}
              readOnly={!editable || pending}
              onChange={(value) => {
                edit({ ...draft, stopAfterDays: value }, "stopAfterDays");
              }}
            />
          </Group>

          {errors.body ? (
            <p role="alert" className="text-destructive text-sm">
              {errors.body}
            </p>
          ) : null}
        </CardContent>

        {editable ? (
          // On a phone it sticks to the foot of the scrolling page, so the button
          // is above the on-screen keyboard and not a scroll below the field. A
          // desktop shows the whole form at once and needs no such bar over it.
          <div className="bg-card space-y-2 border-t px-4 py-3 max-md:sticky max-md:bottom-0 max-md:z-10 sm:px-6">
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="submit"
                disabled={!dirty || pending}
                aria-busy={pending}
                className="pointer-coarse:h-11 pointer-coarse:px-5"
              >
                {pending ? (
                  <Loader2 className="animate-spin" aria-hidden />
                ) : null}
                {pending ? "Saving…" : "Save settings"}
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={pending}
                className="pointer-coarse:h-11 pointer-coarse:px-5"
                onClick={() => {
                  setDraft(toDraft(defaults));
                  setErrors({});
                  setMessage({
                    tone: "info",
                    text: "Defaults filled in. Save to apply them.",
                  });
                }}
              >
                Reset to defaults
              </Button>
            </div>
            <p className="text-muted-foreground text-xs">
              Saving re-judges every open shipment&apos;s health against these
              limits straight away.
            </p>
            <OutcomeLine message={message} />
          </div>
        ) : null}
      </Card>
    </form>
  );
}

function Group({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <fieldset className={cn("min-w-0 space-y-3")}>
      <legend className="text-sm font-semibold">{title}</legend>
      <p className="text-muted-foreground max-w-prose text-sm">{description}</p>
      {children}
    </fieldset>
  );
}

/**
 * Settings → Logistics partners, the tracking settings (#527, Part B): how
 * often to look, how long a stage may be quiet before a shipment is flagged,
 * whether Sundays count, and when to give up on a parcel nobody hears from.
 *
 * Every number says its unit and its default and the range the server will take.
 * A refusal is the server's own message, under the field it names. Whoever may
 * see but not change them reads the same form with its fields locked and no
 * button; the server refuses anyone else regardless of what this draws.
 */
export function TrackingSettingsForm() {
  const data = useDataSource();
  const query = useQuery({
    queryKey: ["tracking", "settings"],
    queryFn: () => data.tracking.getSettings(),
  });

  return (
    <section aria-labelledby="settings-heading" className="space-y-3">
      <div className="max-w-prose">
        <h2 id="settings-heading" className="text-base font-semibold">
          Tracking settings
        </h2>
        <p className="text-muted-foreground text-sm">
          The rules for when a shipment is flagged as needing a look.
        </p>
      </div>
      {query.isPending ? (
        <TableSkeleton rows={4} />
      ) : query.isError ? (
        <LoadFailed
          message={query.error.message}
          onRetry={() => {
            void query.refetch();
          }}
        />
      ) : (
        <Form state={query.data} />
      )}
    </section>
  );
}
